"use client"

import styled from "@emotion/styled"
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material"
import { useTranslations } from "next-intl"
import Link from "next/link"
import { useState } from "react"
import { ArrowDown, PdfIcon } from "ui"
import NewText from "ui/NewText/NewText"
import { extractProperty, getUmbracoMediaUrl } from "utils"

const StyledWrapper = styled.div`
  padding-top: 32px;

  ${(props) => props.theme.breakpoints.up("md")} {
    padding-top: 0;
  }
`

const StyledList = styled(NewText)`
  p {
    margin: 16px 0;
  }
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  li {
    position: relative;
    padding-left: 20px;

    &::before {
      content: "";
      position: absolute;
      left: 0;
      top: 0;
      width: 16px;
      height: 16px;
      background: url('data:image/svg+xml;charset=utf-8,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M13.3346 4L6.0013 11.3333L2.66797 8" stroke="%23${(
  p,
) =>
    p.theme.colors.buttonActiveBackgroundColor?.replace(
      "#",
      "",
    )}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>')
        no-repeat center;
    }
  }
  &.ingredients p {
    margin: 0;
  }
  &.usageInstructions ul li::before {
    content: "";
    width: 6px;
    height: 6px;
    left: 5.5px;
    background: url('data:image/svg+xml;charset=utf-8,<svg xmlns="http://www.w3.org/2000/svg" width="6" height="6" viewBox="0 0 6 6" fill="none"><circle cx="3" cy="3" r="2.5" fill="%23${(
      p,
    ) => p.theme.colors.buttonActiveBackgroundColor?.replace("#", "")}"/></svg>')
      no-repeat center;
  }
  &.safetyGuidelines ul li::before {
    content: "";
    width: 16px;
    height: 16px;
    background: url('data:image/svg+xml;charset=utf-8,<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14Z" stroke="%23${(
      p,
    ) =>
    p.theme.colors.buttonActiveBackgroundColor?.replace(
      "#",
      "",
    )}" stroke-miterlimit="10"/><path d="M8 5V8.5" stroke="%23${(p) =>
      p.theme.colors.buttonActiveBackgroundColor?.replace(
        "#",
        "",
      )}" stroke-linecap="round" stroke-linejoin="round"/><path d="M8 11.5C8.41421 11.5 8.75 11.1642 8.75 10.75C8.75 10.3358 8.41421 10 8 10C7.58579 10 7.25 10.3358 7.25 10.75C7.25 11.1642 7.58579 11.5 8 11.5Z" fill="%23${(
        p,
      ) => p.theme.colors.buttonActiveBackgroundColor?.replace("#", "")}"/></svg>')
      no-repeat center;
  }
  table {
    background: var(--Light-grey, #f7f7f7);
    border: none;
    td {
      border: none;
      font-weight: 400;
      padding: 14px 16px 14px 8px;
      font-size: 14px;
      ${(props) => props.theme.breakpoints.up("md")} {
        font-size: 18px;
        padding: 21px 16px 21px 12px;
      }
      &:first-child {
        font-weight: 500;
        padding: 14px 8px 14px 16px;
        ${(props) => props.theme.breakpoints.up("md")} {
          padding: 21px 12px 21px 16px;
        }
      }
    }
  }
`

const StyledAccordionSummary = styled(AccordionSummary)`
  padding: 16px 0;

  .Mui-expanded {
    margin: 0;
  }
  &.Mui-expanded {
    padding: 16px 0 0;
    ${(props) => props.theme.breakpoints.up("md")} {
      padding: 16px 0 8px;
    }
  }
  svg {
    color: var(--Black, #000);
  }
`

const StyledAccordionDetails = styled(AccordionDetails)`
  padding: 16px 0;
`

const StyledDocumentsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`
const StyledDocument = styled.li`
  a {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;
    color: var(--Black, #000);
    color: #000b15;
    text-align: right;
    font-size: 18px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    text-decoration-line: underline;
    text-decoration-style: solid;
    text-decoration-skip-ink: none;
    text-decoration-thickness: auto;
    text-underline-offset: auto;
    text-underline-position: from-font;
  }
`

const AccordionBlock = ({ data }: { data: any }) => {
  const t = useTranslations("ProductsPage")
  const properties = extractProperty(data, "prodocutProperties")
  const dataTable = extractProperty(data, "dataTable")
  const ingredients = extractProperty(data, "ingredients")
  const usageInstructions = extractProperty(data, "usageInstructions")
  const advantages = extractProperty(data, "advantages")
  const safetyGuidelines = extractProperty(data, "safetyGuidelines")
  const technicalDocuments = data?.properties
    ?.find((property: any) => property?.propertyName === "technicalDocuments")
    ?.propertyValue?.mediaItems?.map((media: any) => {
      const LRM = "\u200E"
      return {
        url: media?.url,
        name: `${LRM}${media?.url?.split("/")?.[5]}`,
      }
    })

  type AccordionItemName =
    | "properties"
    | "dataTable"
    | "ingredients"
    | "usageInstructions"
    | "advantages"
    | "safetyGuidelines"
    | "technicalDocuments"

  const accordionList: { name: AccordionItemName; content: any }[] = [
    {
      name: "properties" as AccordionItemName,
      content: properties,
    },
    {
      name: "dataTable" as AccordionItemName,
      content: dataTable,
    },
    {
      name: "ingredients" as AccordionItemName,
      content: ingredients,
    },
    {
      name: "usageInstructions" as AccordionItemName,
      content: usageInstructions,
    },
    {
      name: "advantages" as AccordionItemName,
      content: advantages,
    },
    {
      name: "safetyGuidelines" as AccordionItemName,
      content: safetyGuidelines,
    },
    {
      name: "technicalDocuments" as AccordionItemName,
      content: technicalDocuments,
    },
  ]?.filter((item) => item.content && item.content?.length !== 0)

  const [activeAccordion, setActiveAccordion] = useState<number | null>(null)

  if (accordionList?.length === 0) {
    return null
  }
  return (
    <StyledWrapper>
      {accordionList?.map((item, index) => {
        if (!item) return null
        return (
          <Accordion
            key={index}
            expanded={activeAccordion === index}
            onChange={() => {
              setActiveAccordion((prev) => (prev === index ? null : index))
            }}
            sx={{
              boxShadow: "none",
              backgroundColor: "transparent",
              display: "flex",
              flexDirection: "column",
              gap: activeAccordion === index ? "8px" : "0",
              marginBottom: "8px",
              "&.Mui-expanded:before": {
                opacity: 0,
              },
              "&:before": {
                opacity: 0,
              },
              "&.Mui-expanded": {
                margin: "0",
              },
              "&.Mui-expanded:after": {
                opacity: item.name === "dataTable" ? 0 : 1,
              },
              "&:after": {
                position: "relative",
                right: 0,
                bottom: "0",
                left: 0,
                height: "1px",
                content: `""`,
                opacity: 1,
                backgroundColor: "rgba(0, 0, 0, 0.12)",
                transition:
                  "opacity 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
              },
              "&:last-child.Mui-expanded:after": {
                opacity: 1,
              },
            }}
          >
            <StyledAccordionSummary
              aria-controls="panel1a-content"
              id="panel1a-header"
              style={{
                border: "none",
                outline: "none",
                color: "black",
                backgroundColor: "white",
                borderRadius: 0,
                height: "61px",
                minHeight: "61px",
              }}
              expandIcon={<ArrowDown />}
            >
              <NewText
                component={"h2"}
                desktopFontSize={20}
                desktopFontWeight={600}
                desktopLineHeight={"100%"}
                mobileFontSize={18}
                mobileLineHeight={1}
                color="black"
              >
                {t(item?.name)}
              </NewText>
            </StyledAccordionSummary>
            <StyledAccordionDetails
              style={{ paddingTop: item.name === "ingredients" ? "4px" : "16px" }}
            >
              {item.name === "technicalDocuments" ? (
                <StyledDocumentsList>
                  {item.content?.map((doc: { url: string; name: string }, docIndex: number) => (
                    <StyledDocument key={docIndex}>
                      <Link
                        href={getUmbracoMediaUrl(doc.url)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <PdfIcon width={20} height={23} />
                        {doc.name}
                      </Link>
                    </StyledDocument>
                  ))}
                </StyledDocumentsList>
              ) : (
                <StyledList
                  component={"div"}
                  desktopFontSize={18}
                  desktopFontWeight={400}
                  desktopLineHeight={"100%"}
                  mobileFontSize={16}
                  mobileLineHeight={1}
                  color="black"
                  dangerouslySetInnerHTML={{ __html: item.content || "" }}
                  className={item.name}
                />
              )}
            </StyledAccordionDetails>
          </Accordion>
        )
      })}
    </StyledWrapper>
  )
}

export default AccordionBlock
